import type { ISceneLoaderProgressEvent } from '@babylonjs/core/Loading/sceneLoader';
import type { IFileRequest } from '@babylonjs/core/Misc/fileRequest';
import {
  DecodeBase64UrlToBinary,
  IsBase64DataUrl,
  LoadFileError,
} from '@babylonjs/core/Misc/fileTools';
import type { WebRequest } from '@babylonjs/core/Misc/webRequest';
import type { Scene } from '@babylonjs/core/scene';
import type { Nullable } from '@babylonjs/core/types';

interface IFileRequestInfo extends IFileRequest {
  _lengthComputable?: boolean;
  _loaded?: number;
  _total?: number;
}

export class FileLoader {
  /** @internal */
  public readonly scene: Scene;

  private disposed = false;

  /**
   * Create a new scene context.
   * @param scene - Fx scene.
   */
  constructor(scene: Scene) {
    this.scene = scene;
  }

  // glTFLoader.ts

  private _rootUrl: Nullable<string> = '';

  /**
   * Function called before loading a url referenced by the asset.
   */
  public preprocessUrlAsync = (url: string) => Promise.resolve(url);

  /**
   * Loads a file from uri.
   * @param context - The context when loading the asset
   * @param uri - The base64 or relative uri
   * @returns A promise that resolves with the loaded data when the load is complete
   */
  public loadUriAsync(context: string, uri: string): Promise<ArrayBufferView> {
    // No extensions.
    // const extensionPromise = this._extensionsLoadUriAsync(context, property, uri);
    // if (extensionPromise) {
    //     return extensionPromise;
    // }

    // No validations.
    // if (!this._ValidateUri(uri)) {
    //     throw new Error(`${context}: '${uri}' is invalid`);
    // }

    if (IsBase64DataUrl(uri)) {
      const data = new Uint8Array(DecodeBase64UrlToBinary(uri));
      // this.log(`${context}: Decoded ${uri.substr(0, 64)}... (${data.length} bytes)`);
      return Promise.resolve(data);
    }

    // this.log(`${context}: Loading ${uri}`);

    return this.preprocessUrlAsync(this._rootUrl + uri).then(url => {
      return new Promise((resolve, reject) => {
        this._loadFile(
          this.scene,
          url,
          data => {
            if (!this.disposed) {
              // this.log(`${context}: Loaded ${uri} (${(data as ArrayBuffer).byteLength} bytes)`);
              resolve(new Uint8Array(data as ArrayBuffer));
            }
          },
          true,
          request => {
            reject(
              new LoadFileError(
                `${context}: Failed to load '${uri}'${
                  request ? `: ${request.status} ${request.statusText}` : ''
                }`,
                request,
              ),
            );
          },
        );
      });
    });
  }

  // glTFFileLoader.ts

  private _progressCallback?: (event: ISceneLoaderProgressEvent) => void;
  private readonly _requests: IFileRequestInfo[] = [];

  private _loadFile(
    scene: Scene,
    fileOrUrl: File | string,
    onSuccess: (data: string | ArrayBuffer) => void,
    useArrayBuffer?: boolean | undefined,
    onError?: (request?: WebRequest) => void,
    onOpened?: (request: WebRequest) => void,
  ): IFileRequest {
    const request = scene._loadFile(
      fileOrUrl,
      onSuccess,
      event => {
        this._onProgress(event, request);
      },
      true,
      useArrayBuffer,
      onError,
      onOpened,
    ) as IFileRequestInfo;
    request.onCompleteObservable.add(request => {
      this._requests.splice(this._requests.indexOf(request), 1);
    });
    this._requests.push(request);
    return request;
  }

  private _onProgress(event: ProgressEvent, request: IFileRequestInfo): void {
    if (!this._progressCallback) {
      return;
    }

    request._lengthComputable = event.lengthComputable;
    request._loaded = event.loaded;
    request._total = event.total;

    let lengthComputable = true;
    let loaded = 0;
    let total = 0;
    for (const request of this._requests) {
      if (
        request._lengthComputable === undefined ||
        request._loaded === undefined ||
        request._total === undefined
      ) {
        return;
      }

      lengthComputable = lengthComputable && request._lengthComputable;
      loaded += request._loaded;
      total += request._total;
    }

    this._progressCallback({
      lengthComputable,
      loaded,
      total: lengthComputable ? total : 0,
    });
  }
}
